"use client";

import { useSearchParams, useRouter, usePathname } from "next/navigation";
import { useState, useRef, useEffect, Suspense } from "react";
import { ChevronDown, Check } from "lucide-react";
import { saveSource } from "@/hooks/useCurrentSource";
import { sourceUrl, sourceFromPathname } from "@/lib/source-url";
import Image from "next/image";
import { ENABLE_KALSHI, ENABLE_HYPERLIQUID } from "@/lib/feature-flags";
import { usePredictEnabled } from "@/hooks/usePredictEnabled";

type Source = "polymarket" | "kalshi" | "predict" | "hyperliquid";

interface SourceOption {
  id: Source;
  label: string;
  logo: string;
  color: string;
  /** Logo container background. Defaults to `color`. HL needs a dark
   * chip because brand mint vs mint logo washes out. */
  logoBg?: string;
  enabled: boolean;
  /** Visible but not clickable — renders dimmed with a "coming soon"
   * tooltip. Use for platforms we want to tease before launch. */
  comingSoon?: boolean;
}

const BASE_SOURCES: SourceOption[] = [
  {
    id: "polymarket",
    label: "Polymarket",
    logo: "/logos/polymarket/icon-white.png",
    color: "#2E5CFF",
    enabled: true,
  },
  {
    id: "kalshi",
    label: "Kalshi",
    logo: "/logos/kalshi/k-icon.svg",
    color: "#09C285",
    enabled: ENABLE_KALSHI,
  },
  {
    id: "hyperliquid",
    label: "Hyperliquid",
    logo: "/logos/hyperliquid/symbol-mint.svg",
    color: "#97FCE4",
    logoBg: "#0f1a1f",
    enabled: ENABLE_HYPERLIQUID,
  },
  {
    id: "predict",
    label: "Predict.fun",
    // Empty logo triggers the "P" text fallback (black P on the chip
    // background), matching the predict badge style used in EventCard.
    logo: "",
    color: "#6366F1",
    // Predict: enabled/comingSoon set PER-USER at render in SourceSelectorContent
    // (usePredictEnabled — allowlist via NEXT_PUBLIC_PREDICT_ALLOWLIST, default
    // zeroxitadori). Static default here is OFF/coming-soon.
    enabled: false,
    comingSoon: true,
  },
];

function SourceSelectorContent({ dropUp = false, iconOnly = false, onSourceChange }: { dropUp?: boolean; iconOnly?: boolean; onSourceChange?: () => void }) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredSource, setHoveredSource] = useState<Source | null>(null);
  const [mounted, setMounted] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Per-user Predict access: flip the Predict chip from coming-soon to enabled
  // only for allowlisted users (or the global flag). Everything else is static.
  const predictEnabled = usePredictEnabled();
  const sources = predictEnabled
    ? BASE_SOURCES.map((s) => (s.id === "predict" ? { ...s, enabled: true, comingSoon: false } : s))
    : BASE_SOURCES;

  useEffect(() => { setMounted(true); }, []);

  // Get current source from URL prefix → query param → pathname → localStorage → default
  const prefixSource = sourceFromPathname(pathname);

  const urlSource = searchParams.get("source") || searchParams.get("_source");
  const normalizedSource = urlSource;

  // Detect source from path: /event/kalshi/... or /event/predict/... or /hyperliquid*
  const pathSource = pathname.startsWith("/event/kalshi/")
    ? "kalshi"
    : pathname.startsWith("/event/predict/")
    ? "predict"
    : (pathname === "/hyperliquid" || pathname.startsWith("/hyperliquid/"))
    ? "hyperliquid"
    : null;

  const detectedSource = prefixSource || normalizedSource || pathSource;
  const validSources = ["polymarket", "kalshi", "predict", "hyperliquid"];
  const savedSource = typeof window !== "undefined" ? localStorage.getItem("frenflow_source") : null;
  const currentSource: Source =
    detectedSource && validSources.includes(detectedSource)
      ? (detectedSource as Source)
      : savedSource && validSources.includes(savedSource)
        ? (savedSource as Source)
        : "polymarket";

  // Optimistic display: when the user picks a source we paint the chip
  // with the new logo + label immediately, before Next.js finishes
  // navigating to /<source>/markets/... . The pending source is cleared
  // when the URL catches up (currentSource matches) or after a 4s
  // safety timeout in case navigation aborts.
  const [pendingSource, setPendingSource] = useState<Source | null>(null);
  useEffect(() => {
    if (!pendingSource) return;
    if (currentSource === pendingSource) {
      setPendingSource(null);
      return;
    }
    const t = setTimeout(() => setPendingSource(null), 4000);
    return () => clearTimeout(t);
  }, [pendingSource, currentSource]);

  const displaySource = pendingSource ?? currentSource;
  const currentOption = sources.find((s) => s.id === displaySource) || sources[0];
  // Include coming-soon sources so users see the platforms we're about
  // to launch; click handling below renders them dimmed + non-interactive.
  const enabledSources = sources.filter((s) => s.enabled || s.comingSoon);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Prefetch destination routes for the OTHER sources. Switching source
  // navigates to a different path segment (`/polymarket/markets/...` →
  // `/kalshi/markets/...`), so Next.js unmounts the current page and
  // mounts a cold one — the RSC payload + page bundle are fetched at
  // click time. Warming them in advance gets the navigation closer to
  // instant. router.prefetch dedupes, so calling this from both hover
  // and dropdown-open is cheap.
  const prefetchOtherSources = () => {
    const stripped = pathname.replace(/^\/(polymarket|kalshi|predictfun|hyperliquid)\//, '/');
    const isMarketPage = stripped.startsWith('/markets') || /^\/(market|event)\//.test(stripped);
    const isDetailPage = /^\/(market|event)\//.test(stripped);
    const targetPath = isDetailPage ? '/markets/trending' : (isMarketPage ? stripped : '/markets/trending');
    for (const s of sources) {
      if (!s.enabled || s.id === currentSource) continue;
      const url = s.id === 'hyperliquid' ? '/hyperliquid/markets' : sourceUrl(targetPath, s.id);
      router.prefetch(url);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    prefetchOtherSources();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, pathname, currentSource, router]);

  // Prevent hydration mismatch — don't render until client-side mount
  if (!mounted) return <div className={iconOnly ? "w-[30px]" : "w-[160px] h-[34px]"} />;

  // Don't show if only one source is enabled
  if (enabledSources.length <= 1) {
    return null;
  }

  const handleSourceChange = (source: Source) => {
    setIsOpen(false);
    // Kalshi traders surface was removed (no copytrading on Kalshi). On the
    // traders pages there's no Kalshi destination, so picking Kalshi here is a
    // no-op — don't save the source or flip the chip, just keep the user on the
    // Polymarket leaderboard. (Kalshi is still a valid source for markets.)
    const onTradersPage = pathname.startsWith("/traders") || !!pathname.match(/^\/(polymarket|kalshi)\/traders/);
    if (source === "kalshi" && onTradersPage) {
      return;
    }
    saveSource(source as any);
    // Optimistic UI: flip the chip's label/logo before navigation lands
    // so the user never sees "Polymarket" sitting above a Kalshi-bound
    // skeleton. Cleared by the effect above once the URL syncs.
    setPendingSource(source);
    // Dispatch custom event so MarketsPageContent can react immediately
    window.dispatchEvent(new CustomEvent('frenflow:source-change', { detail: { source } }));
    onSourceChange?.();

    // On portfolio, stay on portfolio (unified view — no source param needed)
    if (pathname.startsWith("/portfolio")) {
      router.push("/portfolio");
      return;
    }

    // On traders pages, navigate to source-specific traders
    if (pathname.startsWith("/traders") || pathname.match(/^\/(polymarket|kalshi)\/traders/)) {
      if (source === "polymarket") {
        router.push("/traders");
      } else if (source === "predict") {
        router.push("/traders/predict");
      } else if (source === "hyperliquid") {
        // Traders surface for HL not built yet — land on the markets list
        router.push("/hyperliquid");
      }
      return;
    }

    // Hyperliquid follows the {source}/markets convention. /hyperliquid is
    // a server redirect to /hyperliquid/markets — push there directly so the
    // user doesn't see the redirect bounce.
    if (source === "hyperliquid") {
      router.push("/hyperliquid/markets");
      return;
    }

    // Only navigate for pages that support source-prefixed routes (markets, events)
    const stripped = pathname.replace(/^\/(polymarket|kalshi|predictfun|hyperliquid)\//, '/');
    const isMarketPage = stripped.startsWith('/markets') || /^\/(market|event)\//.test(stripped);

    if (isMarketPage) {
      // For market/event detail pages, go to trending instead
      const isDetailPage = /^\/(market|event)\//.test(stripped);
      const targetPath = isDetailPage ? '/markets/trending' : stripped;
      router.push(sourceUrl(targetPath, source));
    }
    // For all other pages (activity, copytrading, etc.) just save the preference — no navigation
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Selected Source Button — fixed width so layout never shifts */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={prefetchOtherSources}
        onFocus={prefetchOtherSources}
        className={`flex items-center justify-center rounded-[5px] transition-colors ${iconOnly ? 'p-1.5' : 'w-[160px] gap-2 pl-2.5 pr-2.5 py-1.5'} bg-black/[0.04] dark:bg-white/[0.06] hover:bg-black/[0.12] dark:hover:bg-white/[0.14]`}
      >
        <div className="w-5 h-5 rounded-[5px] flex items-center justify-center flex-shrink-0" style={{ backgroundColor: currentOption.logoBg ?? currentOption.color }}>
          {currentOption.logo ? (
            <Image
              src={currentOption.logo}
              alt={currentOption.label}
              width={14}
              height={14}
              className="object-contain"
            />
          ) : (
            <span className="text-[11px] font-black text-black leading-none">P</span>
          )}
        </div>
        {!iconOnly && (
          <>
            <span className="text-[13px] font-semibold text-gray-900 dark:text-white">{currentOption.label}</span>
            <ChevronDown
              className={`w-3.5 h-3.5 text-gray-400 dark:text-gray-500 transition-transform ${isOpen ? "rotate-180" : ""}`}
            />
          </>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className={`absolute ${dropUp ? 'bottom-full mb-1.5' : 'top-full mt-1.5'} right-0 min-w-full w-max py-1 bg-white dark:bg-[#18181D] border border-gray-200 dark:border-white/[0.08] rounded-[5px] shadow-lg z-50`}>
          {enabledSources.map((source) => {
            const isSelected = displaySource === source.id;
            const isHovered = hoveredSource === source.id;
            const isComingSoon = !!source.comingSoon;

            return (
              <button
                key={source.id}
                onClick={() => { if (!isComingSoon) handleSourceChange(source.id); }}
                onMouseEnter={() => setHoveredSource(source.id)}
                onMouseLeave={() => setHoveredSource(null)}
                aria-disabled={isComingSoon}
                className={`relative w-full flex items-center gap-2.5 px-3 py-2 transition-colors ${
                  isComingSoon
                    ? 'opacity-50 cursor-default'
                    : isSelected
                    ? 'bg-gray-50 dark:bg-white/[0.06]'
                    : isHovered
                    ? 'bg-gray-50 dark:bg-white/[0.04]'
                    : ''
                }`}
              >
                <div className="w-6 h-6 rounded-[5px] flex items-center justify-center flex-shrink-0" style={{ backgroundColor: source.logoBg ?? source.color }}>
                  {source.logo ? (
                    <Image
                      src={source.logo}
                      alt={source.label}
                      width={16}
                      height={16}
                      className="object-contain"
                    />
                  ) : (
                    <span className="text-[13px] font-black text-black leading-none">P</span>
                  )}
                </div>
                <span className="text-[13px] font-semibold text-gray-900 dark:text-white flex-1 text-left">
                  {source.label}
                </span>
                {!isComingSoon && isSelected && (
                  <Check className="w-3.5 h-3.5 text-[#CCFF00]" />
                )}
                {isComingSoon && isHovered && (
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 px-2 py-1 rounded-[5px] bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-[10px] font-semibold whitespace-nowrap shadow-md pointer-events-none z-10">
                    Coming soon
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Wrap in Suspense for SSR compatibility (useSearchParams requires it)
export default function SourceSelector({ dropUp = false, iconOnly = false, onSourceChange }: { dropUp?: boolean; iconOnly?: boolean; onSourceChange?: () => void } = {}) {
  return (
    <Suspense fallback={<div className={iconOnly ? "w-[30px]" : "w-[160px] h-[34px]"} />}>
      <SourceSelectorContent dropUp={dropUp} iconOnly={iconOnly} onSourceChange={onSourceChange} />
    </Suspense>
  );
}
